<template>
  <div>
    <div
      v-for="field in fieldComponents"
      :key="field.id"
      class="df-field"
      :class="{
        'has-value': getModelValue(field.schema),
        'has-focus': hasFocus || autofilled,
      }"
    >
      <component
        :is="field.component"
        :id="field.id"
        :ref="field.key"
        :data-df-key="field.key"
        :data-full-story="field.showFullStory"
        :locale="locale"
        :schema="field.schema"
        :value="getModelValue(field.schema)"
        @input="setModelValue(field.schema, $event)"
        @focus="hasFocus = true"
        @blur="hasFocus = false"
        @animationstart="checkAnimation"
      />
    </div>
  </div>
</template>

<script>
/**
 * Original Author: Aaron Turnblom
 * Original Team: Area 51
 */
// this component dynamically loads a schema JS file
// which describes a typed set of input fields. One
// row element is created for each input field.
import * as fields from './fields/index.js';
import './common/schema-typedefs.js';

import { LocaleMixin } from './mixins/index.js';

const COMPONENT_MAPPING = {
  autocomplete: fields.DfAutocomplete,
  checkbox: fields.DfCheckbox,
  'date-picker': fields.DfDatePicker,
  hidden: fields.DfHidden,
  label: fields.DfLabel,
  password: fields.DfPassword,
  'radio-group': fields.DfRadioGroup,
  select: fields.DfSelect,
  telephone: fields.DfTelephone,
  'text-field': fields.DfTextField,
};

let fieldsetInstances = 0;

export default {
  name: 'NsDynamicFieldset',
  mixins: [LocaleMixin],
  props: {
    componentMapper: {
      type: Function,
      default: undefined,
    },
    /**
     * Schema object defining this fieldset's fields and layout
     * @type {FieldsetSchema}
     */
    schema: {
      type: Object,
      default: () => {},
    },
    value: {
      type: Object,
    },
  },
  data() {
    return {
      model: {},
      hasFocus: false,
      autofilled: false,
    };
  },
  computed: {
    fieldComponents() {
      if (!this.schema || !this.schema.fields) {
        return [];
      }
      return this.schema.fields.map((fieldSchema, idx) => {
        const fieldKey = fieldSchema.key || 'label_' + idx;
        const uniqueId = `df${this.instanceId}_${fieldKey}`;
        const showFullStory =
          fieldSchema.key === 'primary_name_default_family' ||
          fieldSchema.key === 'primary_name_default_given' ||
          fieldSchema.key === 'primary_address_local' ||
          fieldSchema.key === 'primary_legal_ids_0' ||
          fieldSchema.key === 'primary_phone_mobile'
            ? true
            : false;
        return {
          component: this.componentFor(fieldSchema),
          schema: fieldSchema,
          key: fieldKey,
          id: uniqueId,
          showFullStory: showFullStory,
        };
      });
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(value) {
        this.model = value || {};
      },
    },
    model: {
      deep: true,
      handler() {
        this.notifyModelChanged();
      },
    },
  },
  beforeCreate() {
    this.instanceId = fieldsetInstances++;
  },
  methods: {
    componentFor(schema) {
      const mapped = this.getMappedComponent(schema);
      const slotted = this.getSlotComponent(schema);
      const builtIn = this.getBuiltInComponent(schema);
      if (mapped) {
        return mapped;
      } else if (slotted) {
        return slotted;
      } else if (builtIn) {
        return builtIn;
      } else {
        return fields.DfLabel;
      }
    },

    // v-model doesn't like dynamic keys, so we do this
    getModelValue({ key }) {
      return key ? this.model[key] : null;
    },
    setModelValue({ key }, value) {
      this.$set(this.model, key, value);
      this.notifyModelChanged();
      this.notifyFieldModelChanged(key, value);
    },
    notifyModelChanged() {
      this.$emit('input', this.model);
    },
    notifyFieldModelChanged(key, value) {
      this.$emit(`input:${key}`, value);
    },
    getMappedComponent(schema) {
      if (!this.componentMapper) {
        return null;
      }
      const mapping = this.componentMapper(schema);
      if (mapping) {
        return wrapMappedComponent(mapping);
      } else {
        return null;
      }
    },
    getSlotComponent(schema) {
      const slotFn = this.$scopedSlots[schema.inputType];
      if (!slotFn) {
        return null;
      }

      return fields.buildDfSlottedField(schema.inputType, slotFn);
    },
    getBuiltInComponent(schema) {
      const { inputType, type } = schema;

      // work around the fact that the current impl of
      // the AEM forms allows you to create different types
      // of inputs from the 'text' input
      if (inputType === 'text-field' && type !== undefined) {
        switch (type) {
          case 'tel':
            return fields.DfTelephone;
          case 'date':
            return fields.DfDatePicker;
          case 'password':
            return fields.DfPassword;
          case 'hidden':
            return fields.DfHidden;
        }
      }
      return COMPONENT_MAPPING[inputType];
    },
    checkAnimation(e) {
      if (e.animationName == 'onAutoFillStart') {
        this.autofilled = true;
        this.$emit('autoFillStarted', e.animationName);
      } else if (e.animationName == 'onAutoFillCancel') {
        this.autofilled = false;
        this.$emit('autoFillCancel', e.animationName);
      }
    },
  },
};

/**
 * Given a component and some property transforms, returns a simple component
 * which performs those transformations, renders the proper component, and listens
 * to events.
 *
 * @param {{}} opts options
 * @param {{}} opts.component Vue component to wrap
 * @param {PropsMapper} opts.mapProps prop mapping function
 * @param {MappedEvents} opts.on event handlers
 */
function wrapMappedComponent({
  component,
  // We're using a function instead of a static mapping so we can take advantage of
  // Vue's reactive system - by invoking the function at the right time, Vue knows
  // what things we need to render the component, and so it can re-render us only
  // when one of those changes
  mapProps = () => {},
  on = {
    input(event, emitChange) {
      emitChange(event);
    },
  },
}) {
  return {
    props: ['schema', 'value', 'locale'],
    render(h) {
      // Wrap our event handlers
      const emitChange = (value) => this.$emit('input', value);
      const wrappedOn = {};
      Object.entries(on).forEach(([eventName, handler]) => {
        wrappedOn[eventName] = (event) => handler(event, emitChange);
      });

      return h(component, {
        props: {
          ...mapProps({
            schema: this.schema,
            locale: this.locale,
            value: this.value,
          }),
        },
        on: wrappedOn,
      });
    },
  };
}
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/_typographyVariables.scss" as types;

$color-blackhole: map.get(colors.$baseColors, blackhole);

.form-label {
  @include global.p-large-semibold();
  margin-top: 2em;
  margin-bottom: 0.5em;
  color: $color-blackhole;
}

::v-deep {
  font-family: map.get(types.$typography, 'font-family-primary');
  font-weight: 400;
  .v-label {
    line-height: 22px;
  }
}
:-webkit-autofill {
  animation-name: onAutoFillStart;
}
:not(:-webkit-autofill) {
  animation-name: onAutoFillCancel;
}
@keyframes onAutoFillStart {
  from {
  }
  to {
  }
}
@keyframes onAutoFillCancel {
  from {
  }
  to {
  }
}
</style>
